import React from 'react';
import { motion } from 'framer-motion';
import { FiLock, FiX } from 'react-icons/fi';
import { FaFish } from 'react-icons/fa';
import { useGame } from '../../contexts/GameContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SPECIAL_FISH_TYPES } from '../../constants/specialFish';
import { ACHIEVEMENTS_LIST } from '../../constants/achievements';
import ContentDate from '../common/ContentDate';
import SafeIcon from '../common/SafeIcon';
import MokugyoIcon from '../ui/MokugyoIcon';

const SpecialFishModal = ({ onClose }) => {
  const { state, selectSpecialFish } = useGame();
  const { t } = useLanguage();

  const unlockedFish = SPECIAL_FISH_TYPES.filter(
    (fish) => fish.id === 'normal'
      || (
        fish.unlockCondition
        && state.unlockedAchievements.includes(fish.unlockCondition)
      ),
  );

  const getUnlockRequirement = (fish) => {
    if (fish.id === 'normal') {
      return null;
    }

    const achievement = ACHIEVEMENTS_LIST.find(
      (item) => item.id === fish.unlockCondition,
    );

    return (
      t(`achievementsList.${achievement?.id}.description`)
      || achievement?.description
      || t('specialFish.unlockCondition')
    );
  };

  const isUnlocked = (fish) => {
    return fish.id === 'normal'
      || (
        fish.unlockCondition
        && state.unlockedAchievements.includes(fish.unlockCondition)
      );
  };

  const percentage = Math.round(
    (unlockedFish.length / SPECIAL_FISH_TYPES.length) * 100,
  );

  const sortedFish = [...SPECIAL_FISH_TYPES].sort(
    (firstFish, secondFish) => firstFish.order - secondFish.order,
  );

  return (
    <article className="special-fish-modal-content">
      <header className="modal-header-sticky">
        <h2 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
          <SafeIcon icon={FaFish} />
          {t('specialFish.title')}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t('common.close')}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </header>

      <ContentDate />

      <motion.section
        className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 relative overflow-hidden"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        aria-labelledby="special-fish-selection-heading"
      >
        <motion.div
          className="absolute -right-4 -top-4 text-6xl opacity-10"
          animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden="true"
        >
          👆
        </motion.div>

        <div className="relative z-10">
          <h3
            id="special-fish-selection-heading"
            className="font-bold text-blue-800 mb-2 flex items-center gap-2"
          >
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              aria-hidden="true"
            >
              💡
            </motion.span>
            <span>
              {t('specialFish.clickToSelect') || '如何選擇已解鎖的木魚？'}
            </span>
          </h3>
          <p className="text-sm text-blue-700 leading-relaxed">
            {t('specialFish.clickToSelectDesc')
              || '點擊下方任一已解鎖的木魚卡片，即可切換使用該木魚。選中的木魚會顯示 ✓ 標記。'}
          </p>
        </div>

        <motion.div
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-2xl"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          aria-hidden="true"
        >
          ⬇️
        </motion.div>
      </motion.section>

      <section
        className="progress-section mb-6"
        aria-label={t('specialFish.progress')}
      >
        <div className="flex justify-between mb-2">
          <span>{t('specialFish.progress')}</span>
          <span className="font-bold">
            {unlockedFish.length}/{SPECIAL_FISH_TYPES.length}
          </span>
        </div>
        <div
          className="w-full bg-gray-200 rounded-full h-3"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax={SPECIAL_FISH_TYPES.length}
          aria-valuenow={unlockedFish.length}
        >
          <motion.div
            className="bg-gradient-to-r from-blue-400 to-purple-500 h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
      </section>

      <section className="fish-grid grid grid-cols-2 md:grid-cols-3 gap-4">
        {sortedFish.map((fish, index) => {
          const unlocked = isUnlocked(fish);
          const selected = fish.id === state.currentSpecialFish;
          const name = t(`specialFish.types.${fish.id}.name`) || fish.name;
          const description = (
            t(`specialFish.types.${fish.id}.description`) || fish.description
          );

          return (
            <motion.button
              type="button"
              key={fish.id}
              className={`fish-card relative p-4 rounded-lg border-2 transition-all text-left ${
                selected
                  ? 'border-blue-500 bg-blue-50 shadow-lg'
                  : unlocked
                    ? 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                    : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
              onClick={() => unlocked && selectSpecialFish(fish.id)}
              disabled={!unlocked}
              aria-pressed={selected}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              whileHover={unlocked ? { scale: 1.05, y: -4 } : {}}
              whileTap={unlocked ? { scale: 0.98 } : {}}
            >
              {unlocked && !selected && (
                <motion.span
                  className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.05 }}
                >
                  👆 {t('common.clickToSelect') || '點擊'}
                </motion.span>
              )}

              {!unlocked && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                  <SafeIcon icon={FiLock} className="text-2xl text-gray-600" />
                </span>
              )}

              <span className="block text-center mb-3">
                <span
                  className="flex justify-center mb-2"
                  aria-hidden="true"
                >
                  <MokugyoIcon type={fish.id} size={48} />
                </span>
                <span className="block font-bold text-sm">{name}</span>
              </span>

              <span className="block text-xs text-gray-600 text-center mb-3">
                {description}
              </span>

              {!unlocked && (
                <span className="block text-xs text-center p-2 bg-amber-50 rounded border">
                  <span className="block font-medium text-amber-800 mb-1">
                    {t('specialFish.unlockCondition')}:
                  </span>
                  <span className="block text-amber-700">
                    {getUnlockRequirement(fish)}
                  </span>
                </span>
              )}

              {selected && (
                <motion.span
                  className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-md"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  aria-label={t('game.inUse')}
                >
                  ✓
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </section>

      <section className="info-section mt-6 p-4 bg-amber-50 rounded-lg">
        <h3 className="font-bold text-amber-800 mb-2">
          {t('specialFish.howToUnlock')}
        </h3>
        <p className="text-sm text-amber-700">
          {t('specialFish.description')}
        </p>
      </section>
    </article>
  );
};

SpecialFishModal.managesContentDate = true;

export default SpecialFishModal;